import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

/**
 * Paleta do telão: navy/azul/laranja da Fuzzy Robotics League, com o laranja
 * Fuzzy como cor de ação. Contraste pensado para TV a vários metros.
 */
export const colors = {
  background: '#001e3c',
  backgroundDeep: '#00122a',
  panel: 'rgba(255, 255, 255, 0.06)',
  panelStrong: 'rgba(255, 255, 255, 0.1)',
  border: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  muted: 'rgba(255, 255, 255, 0.72)',
  blue: '#1c88e3',
  orange: '#e57513',
  accent: '#ff9800',
  live: '#ff4d4f',
  up: '#22c55e',
  down: '#f87171',
  gold: '#ffc83d',
  silver: '#cfd8e3',
  bronze: '#e0915a',
};

/** Ícone de linha (Myicons) alinhado ao texto; tamanho segue a fonte. */
export const IconText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  vertical-align: middle;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;

  svg {
    width: 1.15em;
    height: 1.15em;
    flex-shrink: 0;
  }
`;

/**
 * Enquanto o telão está aberto, a página inteira usa o fundo dele. No celular,
 * puxar além do topo/fim (rubber-band) mostrava o branco do body por trás.
 */
export const BoardPageBackground = createGlobalStyle`
  /* html body: mais específico que o body do estilo global do app, que é
     registrado depois deste. */
  html,
  html body {
    background: ${colors.backgroundDeep};
  }
`;

export const Screen = styled.div`
  position: relative;
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
  isolation: isolate;
  background:
    radial-gradient(
      ellipse 60% 50% at 0% 0%,
      rgba(28, 136, 227, 0.35),
      transparent 70%
    ),
    radial-gradient(
      ellipse 50% 45% at 100% 100%,
      rgba(229, 117, 19, 0.22),
      transparent 70%
    ),
    linear-gradient(160deg, ${colors.background}, ${colors.backgroundDeep});
  color: ${colors.text};
  font-family: 'RedHatDisplay-Regular', sans-serif;
  display: grid;
  grid-template-rows: auto 1fr auto;
  /* Coluna presa à largura da tela. Sem isso, o conteúdo mais largo (tabela,
     ticker) alargava a página no celular, e o fundo branco aparecia ao rolar
     para o lado. */
  grid-template-columns: minmax(0, 1fr);

  /* Grade técnica bem sutil: textura de "arena" sem competir com o conteúdo. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
    background-size: 4vh 4vh;
    mask-image: radial-gradient(ellipse at 50% 30%, #000 20%, transparent 75%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: visible;
  }
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  gap: clamp(1rem, 2vw, 2.5rem);
  padding: 2vh 2.2vw 1.4vh;

  @media (max-width: 768px) {
    padding: 1rem 1rem 0.6rem;
    gap: 0.8rem;
    flex-wrap: wrap;
  }
`;

export const BrandLogo = styled.img`
  height: clamp(3rem, 9vh, 7rem);
  width: auto;
  flex-shrink: 0;
  filter: drop-shadow(0 0.4rem 1.2rem rgba(0, 0, 0, 0.35));

  @media (max-width: 768px) {
    height: 3.2rem;
  }
`;

export const HeaderDivider = styled.span`
  align-self: stretch;
  width: 2px;
  margin: 1vh 0;
  border-radius: 2px;
  background: linear-gradient(transparent, ${colors.border}, transparent);

  @media (max-width: 768px) {
    display: none;
  }
`;

export const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6vh;

  h1 {
    font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
    font-size: clamp(1.5rem, 4.4vh, 3.6rem);
    line-height: 1.05;
    letter-spacing: -0.02em;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 768px) {
    flex-basis: 100%;
    order: 3;

    h1 {
      white-space: normal;
      font-size: 1.5rem;
    }
  }
`;

const livePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7); }
  70% { box-shadow: 0 0 0 0.6em rgba(255, 77, 79, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
`;

/** Bolinha vermelha "ao vivo" que pulsa. */
export const LiveDot = styled.span<{ $color?: string }>`
  display: inline-block;
  width: 0.6em;
  height: 0.6em;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color }) => $color ?? colors.live};
  animation: ${livePulse} 1.6s ease-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PhaseTag = styled.span<{ $phase: 'before' | 'live' | 'after' }>`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  font-family: 'RedHatDisplay-Bold';
  font-size: clamp(0.85rem, 2vh, 1.5rem);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.3em 0.9em;
  border-radius: 999px;
  border: 1px solid transparent;
  background: ${({ $phase }) =>
    $phase === 'live'
      ? 'rgba(255, 77, 79, 0.16)'
      : $phase === 'after'
        ? 'rgba(255, 200, 61, 0.16)'
        : 'rgba(255, 255, 255, 0.08)'};
  border-color: ${({ $phase }) =>
    $phase === 'live'
      ? 'rgba(255, 77, 79, 0.5)'
      : $phase === 'after'
        ? 'rgba(255, 200, 61, 0.5)'
        : colors.border};
  color: ${({ $phase }) =>
    $phase === 'live'
      ? '#ffb3b4'
      : $phase === 'after'
        ? colors.gold
        : colors.muted};
`;

export const Clock = styled.div`
  font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
  font-size: clamp(1.4rem, 5.4vh, 4.4rem);
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  padding: 0.25em 0.5em;
  border-radius: 0.4em;
  background: ${colors.panel};
  border: 1px solid ${colors.border};

  @media (max-width: 768px) {
    margin-left: auto;
    font-size: 1.3rem;
  }
`;

export const Body = styled.main`
  display: grid;
  grid-template-columns: minmax(0, 2.3fr) minmax(0, 1fr);
  gap: 1.6vw;
  padding: 0 2.2vw;
  min-height: 0;

  @media (max-width: 768px) {
    /* minmax(0, …): a coluna não cresce para caber a tabela. */
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    padding: 0 1rem;
  }
`;

export const Column = styled.section`
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    overflow: visible;
  }
`;

export const PageNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.4vh;
`;

/** Pílula da página; a ativa enche como barra de tempo até a próxima troca. */
export const PageDot = styled.span<{ $active: boolean }>`
  position: relative;
  width: ${({ $active }) => ($active ? '3.2rem' : '0.7rem')};
  height: 0.7rem;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.22);
  transition: width 0.4s cubic-bezier(0.22, 1, 0.36, 1);

  > span {
    position: absolute;
    inset: 0;
    background: ${colors.accent};
    transform-origin: left center;
  }
`;

export const Panel = styled.div`
  background: ${colors.panel};
  border: 1px solid ${colors.border};
  border-radius: 1.1rem;
  padding: 1.6vh 1.1vw;
  margin-bottom: 1.6vh;
  backdrop-filter: blur(12px);

  h2 {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-family: 'RedHatDisplay-Bold';
    font-size: clamp(1rem, 2.6vh, 2rem);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin: 0 0 1vh;
  }

  @media (max-width: 768px) {
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
    backdrop-filter: none;
  }

  @media (prefers-reduced-transparency: reduce) {
    background: #0a2a4c;
    backdrop-filter: none;
  }
`;

export const SlotRow = styled.div<{ $ongoing?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: baseline;
  gap: 0.2rem 0.8rem;
  padding: 0.9vh 0.7vw;
  border-radius: 0.7rem;
  font-size: clamp(0.95rem, 2.3vh, 1.8rem);

  & + & {
    margin-top: 0.4vh;
  }

  strong {
    font-family: 'RedHatDisplay-Bold';
    line-height: 1.15;
  }

  small {
    grid-column: 2;
    color: ${colors.muted};
    font-size: 0.72em;
  }

  ${({ $ongoing }) =>
    $ongoing &&
    css`
      background: linear-gradient(
        90deg,
        rgba(255, 77, 79, 0.2),
        rgba(255, 77, 79, 0.05)
      );
      box-shadow: inset 4px 0 0 ${colors.live};
    `}

  @media (max-width: 768px) {
    padding: 0.55rem 0.6rem;
  }
`;

/** Selo de rodada atrasada em "Próximas" (FE-76). */
export const LateTag = styled.span`
  display: inline-block;
  margin-left: 0.5em;
  padding: 0.05em 0.5em;
  border-radius: 999px;
  background: rgba(248, 113, 113, 0.22);
  color: #fecaca;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.6em;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: middle;
`;

/** Atraso da arena mais atrasada, ao lado do título "Próximas". */
export const DelayNote = styled.span`
  margin-left: auto;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.7em;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  text-transform: none;
  color: #fecaca;
`;

export const SlotTime = styled.span`
  font-family: 'RedHatDisplay-Bold';
  color: ${colors.accent};
  font-variant-numeric: tabular-nums;
`;

/** Lateral da TV: o cronograma encolhe (com fade) para o QR caber sempre. */
export const SideStack = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  mask-image: linear-gradient(#000 calc(100% - 3rem), transparent);

  @media (max-width: 768px) {
    overflow: visible;
    mask-image: none;
  }
`;

export const QrPanel = styled(Panel)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 1.2vw;
  margin-top: 1vh;
  margin-bottom: 0;

  > div:first-child {
    flex-shrink: 0;
  }

  > div:last-child {
    min-width: 0;
  }

  > div:first-child svg {
    display: block;
    width: clamp(5rem, min(15vh, 8vw), 11rem);
    height: auto;
    border-radius: 0.6rem;
  }

  h2 {
    text-transform: none;
    letter-spacing: 0;
    font-size: clamp(0.95rem, 2.2vh, 1.6rem);
    margin-bottom: 0.4vh;
  }

  p {
    margin: 0;
    color: ${colors.muted};
    font-size: clamp(0.85rem, 1.9vh, 1.4rem);
  }
`;

export const Footer = styled.footer`
  display: flex;
  align-items: center;
  gap: 1.4vw;
  padding: 1.2vh 2.2vw 1.6vh;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 1rem;
    flex-wrap: wrap;
    gap: 0.8rem;
  }
`;

const marquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

export const Ticker = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  padding: 0.9vh 0;
  border-radius: 999px;
  background: ${colors.panel};
  border: 1px solid ${colors.border};
  mask-image: linear-gradient(
    90deg,
    transparent,
    #000 3%,
    #000 97%,
    transparent
  );

  @media (max-width: 768px) {
    order: 3;
    flex-basis: 100%;
  }
`;

export const TickerTrack = styled.div<{ $duration: number }>`
  display: inline-flex;
  gap: 3.5rem;
  padding-left: 2rem;
  animation: ${marquee} ${({ $duration }) => $duration}s linear infinite;
  font-size: clamp(0.95rem, 2.2vh, 1.7rem);

  strong {
    font-family: 'RedHatDisplay-Bold';
  }

  em {
    font-style: normal;
    font-family: 'RedHatDisplay-Bold';
    color: ${colors.accent};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const LiveIndicator = styled.span<{
  $status: 'connecting' | 'live' | 'polling' | 'offline';
}>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  font-family: 'RedHatDisplay-Bold';
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: clamp(0.8rem, 1.8vh, 1.3rem);
  color: ${({ $status }) =>
    $status === 'live' || $status === 'polling'
      ? colors.text
      : $status === 'offline'
        ? colors.down
        : colors.muted};
`;

export const PoweredBy = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: clamp(0.7rem, 1.4vh, 1rem);
  color: ${colors.muted};
  letter-spacing: 0.06em;
  text-transform: uppercase;

  svg {
    height: clamp(1.2rem, 3vh, 2.2rem);
    width: auto;
  }
`;

export const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2vh;
  text-align: center;
  height: 100%;
  padding: 2rem;

  h2 {
    font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
    font-size: clamp(1.6rem, 5vh, 4rem);
    letter-spacing: -0.02em;
    line-height: 1.05;
    margin: 0;
  }

  p {
    color: ${colors.muted};
    font-size: clamp(1rem, 2.6vh, 2rem);
    margin: 0;
  }
`;

export const HeroMascot = styled.img`
  height: clamp(7rem, 26vh, 20rem);
  width: auto;
  filter: drop-shadow(0 1.5rem 2.5rem rgba(0, 0, 0, 0.45));
`;

export const Countdown = styled.div`
  display: flex;
  font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
  font-size: clamp(3.5rem, min(18vh, 20vw), 14rem);
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  color: ${colors.accent};
  /* drop-shadow no conjunto: text-shadow seria cortado em cada casa. */
  filter: drop-shadow(0 0 0.25em rgba(255, 152, 0, 0.35));
`;

/** Uma casa do contador: o dígito novo desce e empurra o antigo. */
export const DigitSlot = styled.span`
  position: relative;
  display: inline-block;
  overflow: hidden;
  height: 1em;
  width: 0.62em;
  text-align: center;

  &[data-sep='true'] {
    width: 0.32em;
  }

  > span {
    position: absolute;
    inset: 0;
  }
`;

export const GroupChips = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.6rem;
  margin-bottom: 0.4rem;
  scrollbar-width: none;

  button {
    flex-shrink: 0;
    border: 1px solid ${colors.border};
    background: ${colors.panel};
    color: ${colors.text};
    border-radius: 999px;
    padding: 0.55rem 1rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 0.9rem;
    transition:
      transform 100ms ease-out,
      background 200ms;
    -webkit-tap-highlight-color: transparent;
  }

  button:active {
    transform: scale(0.96);
  }

  button[aria-pressed='true'] {
    background: ${colors.accent};
    border-color: ${colors.accent};
    color: #1a1a1a;
  }
`;

export const Badge = styled.span<{ $tone: 'up' | 'down' | 'leader' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  margin-left: 0.6rem;
  padding: 0.12em 0.55em;
  border-radius: 999px;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.62em;
  letter-spacing: 0.04em;
  vertical-align: middle;
  color: ${({ $tone }) => ($tone === 'leader' ? '#2a1a00' : '#fff')};
  background: ${({ $tone }) =>
    $tone === 'up'
      ? colors.up
      : $tone === 'down'
        ? 'rgba(248, 113, 113, 0.4)'
        : `linear-gradient(120deg, ${colors.gold}, #ffe9a8 50%, ${colors.gold})`};
  background-size: 200% 100%;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;

  svg {
    width: 1.1em;
    height: 1.1em;
  }
`;

const rowFlash = keyframes`
  0% { background-position: 120% 0; }
  100% { background-position: -20% 0; }
`;

const leaderGlow = keyframes`
  0%, 100% { box-shadow: inset 6px 0 0 ${colors.gold}, 0 0 0 0 rgba(255, 200, 61, 0); }
  50% { box-shadow: inset 6px 0 0 ${colors.gold}, 0 0 2.2rem 0 rgba(255, 200, 61, 0.45); }
`;

/**
 * Estados de linha do telão (`rowClassName` da TournamentRanking): quem
 * pontuou recebe um brilho que atravessa a linha; novo líder pulsa em ouro.
 */
export const RankingArea = styled.div`
  /* Referência da página que sai durante a troca (popLayout, FE-79). */
  position: relative;

  td:nth-child(2) > span:first-child {
    font-family: 'RedHatDisplay-Bold';
  }

  /* Um brilho só, atravessando a linha inteira (FE-75). O fundo da linha é
     pintado como um bloco único, posicionado pela linha; no td, cada célula
     fazia a própria varredura. */
  tr.board-scored {
    background-color: rgba(255, 152, 0, 0.16) !important;
    background-image: linear-gradient(
      100deg,
      transparent 30%,
      rgba(255, 152, 0, 0.45) 50%,
      transparent 70%
    ) !important;
    background-size: 250% 100%;
    background-repeat: no-repeat;
    animation: ${rowFlash} 1.1s cubic-bezier(0.22, 1, 0.36, 1) 2;
    box-shadow: inset 6px 0 0 ${colors.accent};
  }

  tr.board-leader {
    background: linear-gradient(
      90deg,
      rgba(255, 200, 61, 0.3),
      rgba(255, 200, 61, 0.08)
    ) !important;
    animation: ${leaderGlow} 1.4s ease-in-out 3;
    box-shadow: inset 6px 0 0 ${colors.gold};
  }

  @media (prefers-reduced-motion: reduce) {
    tr.board-scored {
      background-image: none !important;
    }

    tr.board-scored,
    tr.board-leader {
      animation: none;
    }
  }
`;

const discGradient = {
  1: `linear-gradient(145deg, #ffe08a, ${colors.gold} 45%, #c98a00)`,
  2: `linear-gradient(145deg, #ffffff, ${colors.silver} 45%, #8a99ab)`,
  3: `linear-gradient(145deg, #ffc89c, ${colors.bronze} 45%, #8f4a1c)`,
} as const;

/**
 * Posição em disco (ouro/prata/bronze no pódio). Substitui o emoji de medalha
 * no telão: TVs com navegador embutido muitas vezes não têm fonte de emoji.
 */
export const PositionDisc = styled.span<{ $position: number }>`
  display: inline-grid;
  place-items: center;
  width: 1.7em;
  height: 1.7em;
  border-radius: 50%;
  font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
  font-variant-numeric: tabular-nums;
  line-height: 1;

  ${({ $position }) =>
    $position >= 1 && $position <= 3
      ? css`
          color: #1a1300;
          background: ${discGradient[$position as 1 | 2 | 3]};
          box-shadow:
            0 0.15em 0.5em rgba(0, 0, 0, 0.35),
            inset 0 -0.12em 0 rgba(0, 0, 0, 0.18);
        `
      : css`
          color: ${colors.text};
          background: ${colors.panelStrong};
        `}
`;
